import { ChangeDetectorRef, Component, HostBinding, Input, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { WindowService } from "@merri/common";
import { Subject } from "rxjs";
import { concatMap, filter, takeUntil, tap } from "rxjs/operators";
import { BlobService, CommonWorkflowStep, LoggingService, RedirectService, RoutingService } from "src/app/core";
import { BrandingService } from "src/app/core/services/branding.service";
import { DesignModeStateService } from "src/app/features/design";
import { Showroom, ShowroomLoggingProp, ShowroomWorkflow, ShowroomWorkflowSource, ShowroomWorkflowStep } from "../../../models";
import { ShowroomShowInquiryService } from "../../../services/showroom-show-inquiry.service";
import { ShowroomShortlistService } from "../../services/showroom-shortlist.service";

const Workflow = ShowroomWorkflow.Showroom;
const WorkflowSource = ShowroomWorkflowSource.ShowroomHeader;

const HeaderUnityPadding = 32;

@Component({
  selector: "app-showroom-header",
  templateUrl: "./showroom-header.component.html",
  styleUrls: ["./showroom-header.component.scss"],
  providers: [ShowroomShortlistService],
  standalone: false,
})
export class ShowroomHeaderComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void> = new Subject<void>();

  private _loggingData: {
    [ShowroomLoggingProp.ShowroomName]?: string;
    [ShowroomLoggingProp.ShowroomOwnerName]?: string;
  };

  logoPoweredByUrl: string;
  showBackToWelcomeButton: boolean;
  showShortlist: boolean;

  @Input() showroom: Showroom;

  @HostBinding("style.width") width: string;
  @HostBinding("style.display") display: string;

  constructor(
    private _blobService: BlobService,
    private _redirectService: RedirectService,
    private _showroomShowInquiryService: ShowroomShowInquiryService,
    private _designModeStateService: DesignModeStateService,
    private _routingService: RoutingService,
    private _router: Router,
    private _cdr: ChangeDetectorRef,
    private _windowService: WindowService,
    private _loggingService: LoggingService,
    private _showroomShortlistService: ShowroomShortlistService,
    private _brandingService: BrandingService,
  ) {}

  ngOnInit(): void {
    this._loggingData = {
      [ShowroomLoggingProp.ShowroomName]: this.showroom.name,
      [ShowroomLoggingProp.ShowroomOwnerName]: this.showroom.ownerName,
    };

    this._loggingService.logWorkflow(
      "Showroom header component init",
      "ShowroomHeaderComponent.ngOnInit",
      Workflow,
      CommonWorkflowStep.ComponentLoad,
      WorkflowSource,
      {
        ...this._loggingData,
      },
    );

    this.logoPoweredByUrl = this._brandingService.logoPoweredByUrl;

    this._designModeStateService.isLoaded$
      .pipe(
        tap(isLoaded => {
          if (this.showroom.layouts?.length > 1) {
            this.showBackToWelcomeButton = isLoaded;
            this._cdr.detectChanges();
          }

          if (!isLoaded) {
            this.width = null;
          }
        }),
        filter(isLoaded => isLoaded),
        concatMap(() =>
          this._designModeStateService.unityGameSize$.pipe(
            tap(unityGameSize => {
              if (unityGameSize) {
                this.width = `${unityGameSize.width + HeaderUnityPadding}px`;
                this._cdr.detectChanges();
              }
            }),
          ),
        ),
        takeUntil(this._destroyed$),
      )
      .subscribe();

    this.showShortlist = this._showroomShortlistService.isShortlistSupported();

    if (this._windowService.isOneTimeSmallScreen()) {
      this.display = "none";
    }

    this._windowService
      .observeMax$(1000)
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: e => {
          if (!e.matches) {
            this.display = "block";
          }
        },
      });

    this._loggingService.logWorkflow(
      "Showroom header component init complete",
      "ShowroomHeaderComponent.ngOnInit",
      Workflow,
      CommonWorkflowStep.ComponentLoadComplete,
      WorkflowSource,
      {
        ...this._loggingData,
        ShowBackToWelcomeButton: `${this.showBackToWelcomeButton}`,
        ShowShortlist: `${this.showShortlist}`,
        ShowroomHeaderDisplay: this.display,
      },
    );
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onPoweredByClick(): void {
    this._loggingService.logWorkflow(
      "Showroom header powered by click",
      "ShowroomHeaderComponent.onPoweredByClick",
      Workflow,
      ShowroomWorkflowStep.ShowroomHeaderPoweredByClick,
      WorkflowSource,
      {
        ...this._loggingData,
      },
    );

    this._redirectService.gotoHomeUrl("_blank");
  }

  onInquireNowClick(): void {
    this._loggingService.logWorkflow(
      "Showroom header inquire now click",
      "ShowroomHeaderComponent.onInquireNowClick",
      Workflow,
      ShowroomWorkflowStep.ShowroomInquireNowClick,
      WorkflowSource,
      {
        ...this._loggingData,
      },
    );

    this._showroomShowInquiryService.openShowroomInquiry("ShowroomHeader");
  }

  onViewAnotherRoomClick(): void {
    this._loggingService.logWorkflow(
      "Showroom view another room click",
      "ShowroomHeaderComponent.onViewAnotherRoomClick",
      Workflow,
      ShowroomWorkflowStep.ShowroomHeaderViewAnotherRoomClick,
      WorkflowSource,
      {
        ...this._loggingData,
      },
    );

    this._router.navigate(["../welcome"], { relativeTo: this._routingService.getRoute() });
  }
}
